'use client';

import React, { Component, type ReactNode } from 'react';
import { initPostHog, isPostHogActive, posthog } from '@/lib/analytics/posthog';
import { ErrorFallback } from '@/components/ErrorFallback';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    // Report render/lifecycle crashes so they are visible off-box. Event
    // handler and async errors never reach a React boundary; those are
    // covered by PostHog's exception autocapture (window.onerror /
    // unhandledrejection), configured in src/lib/analytics/posthog.ts.
    //
    // Boot PostHog here rather than relying on PostHogProvider: a crash
    // during the app's very first render fires this in the commit phase,
    // BEFORE any provider effect has run — exactly the case (first-load
    // crash) this report matters most for. initPostHog is idempotent and
    // keeps its own hostname/key gates.
    initPostHog();
    if (isPostHogActive()) {
      posthog.captureException(error, {
        source: 'react_error_boundary',
        component_stack: errorInfo.componentStack ?? undefined,
      });
    }
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <ErrorFallback
          onRetry={() => this.setState({ hasError: false, error: null })}
        />
      );
    }
    return this.props.children;
  }
}
